import { Box, Link, Tooltip, useColorModeValue } from '@chakra-ui/react'
import type { BoxProps, SystemStyleObject } from '@chakra-ui/react'
import React from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { insertInlineCitations } from '../core/parsers/markdownParser'
import { Reference } from '../types/chatTypes'

interface MarkdownContentProps extends Omit<BoxProps, 'children'> {
  content: string
  // When present, numeric `[n](url)` links whose url is a known source render
  // as compact citation chips with the source title on hover, matching chat.
  references?: Reference[]
}

// Plain markdown renderer (GFM, so tables render) without the chat's bubble
// chrome or YouTube embeds. Use where stored message content needs to read
// like it does in chat, e.g. the analytics drawer.
const MarkdownContent: React.FC<MarkdownContentProps> = ({ content, references, ...boxProps }) => {
  // Resolve any inline [S#] markers against the references; persisted answers
  // already store resolved [n](url) links, so this is usually a no-op, but it
  // also strips stray markers rather than rendering them raw.
  const rendered = React.useMemo(
    () => insertInlineCitations(content, references),
    [content, references],
  )

  const citationByUrl = React.useMemo(
    () => new Map((references ?? []).filter(ref => ref.url).map(ref => [ref.url, ref])),
    [references],
  )

  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const tableHeaderBg = useColorModeValue('gray.100', 'gray.700')
  const tableStripeBg = useColorModeValue('gray.50', 'whiteAlpha.100')
  const codeBg = useColorModeValue('gray.50', 'gray.700')
  const linkColor = useColorModeValue('blue.500', 'blue.300')
  const linkHoverColor = useColorModeValue('blue.600', 'blue.400')
  const citationColor = useColorModeValue('blue.600', 'blue.300')

  const sx: SystemStyleObject = {
    '& p': { my: 2 },
    '& p:first-of-type': { mt: 0 },
    '& p:last-of-type': { mb: 0 },
    '& h1, & h2, & h3, & h4, & h5, & h6': { fontWeight: 'bold', my: 3 },
    '& ul, & ol': { ml: 5, my: 2 },
    '& li': { my: 1 },
    '& pre': { bg: codeBg, p: 3, borderRadius: 'md', overflowX: 'auto', my: 2 },
    '& code': { fontFamily: 'mono', fontSize: '0.9em' },
    '& blockquote': { borderLeft: '4px solid', borderColor, pl: 3, my: 2 },
    '& hr': { borderColor, my: 3 },
    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      my: 3,
      fontSize: '0.9em',
      display: 'block',
      overflowX: 'auto',
    },
    '& thead th': { bg: tableHeaderBg, fontWeight: 600, textAlign: 'left' },
    '& tbody tr:nth-of-type(even)': { bg: tableStripeBg },
    '& td, & th': { border: '1px solid', borderColor, padding: '6px 10px' },
  }

  return (
    <Box sx={sx} {...boxProps}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ children, href }: React.ComponentProps<'a'>) => {
            const url = href || ''
            const linkText = React.Children.toArray(children)
              .map(child => (typeof child === 'string' ? child : ''))
              .join('')
              .trim()

            // Citation link: numeric label whose url is a known source. Render
            // as a compact `[n]` chip, title (+ page) on hover — matching chat.
            const citation = citationByUrl.get(url)
            if (/^\d+$/.test(linkText) && citation) {
              const tooltipLabel = citation.page
                ? `${citation.title} · p. ${citation.page}`
                : citation.title
              return (
                <Tooltip label={tooltipLabel} openDelay={300} hasArrow>
                  <Link
                    href={url}
                    target='_blank'
                    rel='noopener noreferrer'
                    color={citationColor}
                    fontSize='0.85em'
                    fontWeight='500'
                    whiteSpace='nowrap'
                    textDecoration='none'
                    _hover={{ textDecoration: 'underline' }}
                  >
                    [{linkText}]
                  </Link>
                </Tooltip>
              )
            }

            return (
              <Link
                href={url}
                target='_blank'
                rel='noopener noreferrer'
                color={linkColor}
                textDecoration='underline'
                _hover={{ color: linkHoverColor }}
              >
                {children}
              </Link>
            )
          },
        }}
      >
        {rendered}
      </ReactMarkdown>
    </Box>
  )
}

export default React.memo(MarkdownContent)
